import moment from "moment";

/** Extract YYYY-MM-DD from ISO datetime without timezone conversion. */
export const extractWallClockDate = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    return value.split("T")[0] || null;
  }
  return null;
};

/** Extract HH:mm from ISO datetime without timezone conversion. */
export const extractWallClockTime = (value) => {
  if (!value) return null;
  if (typeof value === "string" && !value.includes("T")) {
    return value.slice(0, 5);
  }
  if (typeof value === "string") {
    const match = value.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : null;
  }
  return null;
};

/** Format a wall-clock time (HH:mm or HH:mm:ss) for display. */
export const formatWallClockTime = (timeString, { compact = false } = {}) => {
  if (!timeString) return "";

  const wallClock =
    typeof timeString === "string" && timeString.includes("T")
      ? extractWallClockTime(timeString)
      : String(timeString).slice(0, 8);

  if (!wallClock) return "";

  return moment(wallClock, ["HH:mm:ss", "HH:mm"]).format(
    compact ? "h:mm A" : "hh:mm A"
  );
};

export const APP_TIMEZONE =
  process.env.REACT_APP_REGION || "America/Los_Angeles";

export const formatInAppTimezone = (value, localeOptions = {}) => {
  if (!value) return "";

  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) return "";

  return instant.toLocaleString("en-US", {
    timeZone: APP_TIMEZONE,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...localeOptions,
  });
};

/** Format program start/end for display without viewer timezone conversion. */
export const formatProgramDateTimeDisplay = (
  isoDateTime,
  { datePattern = "MM/DD/YYYY", compact = false, timeOverride = null } = {}
) => {
  if (!isoDateTime) return "";

  // Old logic: converted full ISO datetime via moment (browser timezone).
  // return moment(isoDateTime).format(
  //   compact ? `${datePattern.replace("YYYY", "YY")} | h:mm A` : `${datePattern} | hh:mm A`
  // );

  const datePart = extractWallClockDate(isoDateTime);
  const timePart = timeOverride ?? extractWallClockTime(isoDateTime);

  if (!datePart) return "";

  const formattedDate = moment(datePart, "YYYY-MM-DD").format(datePattern);
  if (!timePart) return formattedDate;

  return `${formattedDate} | ${formatWallClockTime(timePart, { compact })}`;
};

/** Format date portion of ISO datetime without viewer timezone conversion. */
export const formatWallClockDate = (
  isoDateTime,
  datePattern = "MM/DD/YYYY"
) => {
  const datePart = extractWallClockDate(isoDateTime);
  if (!datePart) return "";
  return moment(datePart, "YYYY-MM-DD").format(datePattern);
};

export const formatWallClockMonthDayYear = (isoDateTime) =>
  formatWallClockDate(isoDateTime, "MMM D, YYYY");

export const formatProgramDateRangeLabel = (startIso, endIso) => {
  const startPart = extractWallClockDate(startIso);
  const endPart = extractWallClockDate(endIso);

  if (!startPart && !endPart) return "";
  if (!endPart) return formatWallClockMonthDayYear(startIso);
  if (!startPart) return formatWallClockMonthDayYear(endIso);

  const start = moment(startPart, "YYYY-MM-DD");
  const end = moment(endPart, "YYYY-MM-DD");

  if (!start.isValid() && !end.isValid()) return "";
  if (!end.isValid()) return start.format("MMM D, YYYY");
  if (!start.isValid()) return end.format("MMM D, YYYY");
  if (start.isSame(end, "day")) return start.format("MMM D, YYYY");
  if (start.year() === end.year()) {
    return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

export const getProgramStartTime = (program) =>
  program?.start_time ?? extractWallClockTime(program?.start_date);

export const getProgramEndTime = (program) =>
  program?.end_time ?? extractWallClockTime(program?.end_date);

export const formatProgramStartDateTime = (program, options = {}) =>
  formatProgramDateTimeDisplay(program?.start_date, {
    ...options,
    timeOverride: getProgramStartTime(program),
  });

export const formatProgramEndDateTime = (program, options = {}) =>
  formatProgramDateTimeDisplay(program?.end_date, {
    ...options,
    timeOverride: getProgramEndTime(program),
  });

export const EMPTY_DATE_CELL = ". . .";

export function isEmptyDateValue(value, emptyValue = EMPTY_DATE_CELL) {
  return (
    value === "" ||
    value == null ||
    value === undefined ||
    value === emptyValue
  );
}

/** Table/display helper: wall-clock date with empty fallback. */
export function formatWallClockDateCell(
  value,
  { datePattern = "MM/DD/YYYY", emptyValue = EMPTY_DATE_CELL } = {}
) {
  if (isEmptyDateValue(value, emptyValue)) {
    return emptyValue;
  }
  return formatWallClockDate(value, datePattern) || emptyValue;
}

/** Table/display helper: wall-clock date + time with empty fallback. */
export function formatWallClockDateTimeCell(
  value,
  { datePattern = "MM/DD/YYYY", compact = false, emptyValue = EMPTY_DATE_CELL } = {}
) {
  if (isEmptyDateValue(value, emptyValue)) {
    return emptyValue;
  }
  return (
    formatProgramDateTimeDisplay(value, { datePattern, compact }) || emptyValue
  );
}

/** Table/display helper: wall-clock time with empty fallback. */
export function formatWallClockTimeCell(
  value,
  { compact = true, emptyValue = "..." } = {}
) {
  if (isEmptyDateValue(value, emptyValue)) {
    return emptyValue;
  }
  const formatted = formatWallClockTime(
    typeof value === "string" && value.includes("T")
      ? extractWallClockTime(value)
      : value,
    { compact }
  );
  return formatted || emptyValue;
}

/**
 * Compact relative age for live tracking tables ("2 min ago", "20 min ago").
 * Uses real elapsed time from the timestamp (ISO / Date).
 */
export function formatCompactRelativeTimeAgo(
  value,
  { emptyValue = "..." } = {}
) {
  if (isEmptyDateValue(value, emptyValue)) {
    return emptyValue;
  }

  const instant = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(instant.getTime())) {
    return emptyValue;
  }

  const diffSec = Math.max(0, Math.floor((Date.now() - instant.getTime()) / 1000));
  if (diffSec < 45) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? "" : "s"} ago`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

  return moment(instant).format("MMM D, YYYY");
}
